/**
 * What the checkout knows of a shipping provider beyond what the API says of
 * it (store {shipping_options}): the provider's own office map, when it has
 * one a page can embed. A provider without an entry here still works — its
 * pickup points are picked from the list the API gives.
 *
 * map.url(params)      the map's address for a method's office type
 * map.origin           where its messages come from
 * map.officeIdOf(data) the office id in a message the map posts, or null
 */
const speedyType = (officeType) => (officeType === "locker" ? "APT" : "OFFICE")

export const PROVIDERS = {
  // Speedy's office locator: its page posts the picked office's id to the
  // page that embeds it (window.parent.postMessage(officeID, "*"))
  speedy: {
    map: {
      origin: "https://services.speedy.bg",
      url: ({ officeType, language, officeId, city }) => {
        const query = new URLSearchParams({
          lang: language === "bg" ? "bg" : "en",
          countryId: "100",
          officeType: speedyType(officeType),
          pickUp: "1",
          // the map alone: our own list (OfficePicker) already lists the offices
          showOfficesList: "0",
          selectOfficeButtonCaption: language === "bg" ? "Избери" : "Select",
        })
        if (officeId) query.set("officeID", officeId)
        if (city) query.set("siteName", city)
        return `https://services.speedy.bg/office_locator_widget_v3/office_locator.php?${query}`
      },
      // "Избери" on the map sends the office Speedy's getOfficeDetails
      // returned ({ id, name, type: "OFFICE" | "APT", address… }); the list
      // sends the bare id. The map's town search posts { type: "siteSelected" }.
      officeIdOf: (data) => {
        if (typeof data === "number" || (typeof data === "string" && /^\d+$/.test(data))) return String(data)
        if (!data || typeof data !== "object" || data.type === "siteSelected") return null
        const id = data.id ?? data.officeID ?? data.officeId
        return id != null && /^\d+$/.test(String(id)) ? String(id) : null
      },
    },
  },
}

export const providerOf = (id) => PROVIDERS[id] || {}

/**
 * An office's address without what its name already says: Speedy names an
 * office after its town ("СОФИЯ - САН СТЕФАНО ПЛАЗА") and starts the address
 * with it again ("гр. СОФИЯ [1000] ул. …"), so the town goes once.
 */
export function officeAddress(office) {
  const address = String(office?.address || "").trim()
  const town = String(office?.city || String(office?.name || "").split(/\s[-–]\s/)[0] || "").trim()
  if (!address || !town) return address
  const escaped = town.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const lead = new RegExp(`^(гр\\.|с\\.|к\\.к\\.|кв\\.)?\\s*${escaped}\\s*(\\[\\d+\\])?\\s*,?\\s*`, "iu")
  const rest = address.replace(lead, "").trim()
  return rest || address
}
